import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CmsApiService } from '../../core/services/api/cms-api.service';
import { CmsContentMapperService } from '../../core/services/cms-content-mapper.service';
import { EMPTY, forkJoin, from, merge, Observable, of, timer } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TimeService } from '../../core/services/time.service';
import { ContentUpdaterService } from '../../core/services/content-updater/content-updater.service';
import { NotificationFieldsResolverService } from '../../core/modules/notification-center/notification-fields-resolver.service';
import { PlatformService } from '../../core/services/platform.service';
import { UserService } from '../../core/services/user/user.service';
import { BlogCategoryEnum } from '../../core/enums/blog-category.enum';
import { CustomNotificationChanel } from '../../core/services/user/data/notification.data';
import { GlobalStoreName } from '../../core/store';
import { NotificationStoreEntity, NotificationType } from '../../core/store/notification.store';
import { useStore } from 'ngx-unificator/store';
import { NotificationSocketType } from '../../core/modules/notification-center/notification-center.type';
import { getEndMs, getStartMs, isTimeEligible } from '../../core/helpers/blog-notification.helpers';
import { NotificationCenterService } from '../../core/modules/notification-center/notification-center.service';

@Injectable({
  providedIn: 'root'
})
export class BlogService {
  private _api = inject(CmsApiService);
  private _mapper = inject(CmsContentMapperService);
  private _sanitizer = inject(DomSanitizer);
  private _time = inject(TimeService);
  private _contentUpdater = inject(ContentUpdaterService);
  private _notificationFields = inject(NotificationFieldsResolverService);
  private _notifications = inject(NotificationCenterService);
  private _platform = inject(PlatformService);
  private _user = inject(UserService);
  private _destroyRef = inject(DestroyRef);

  private _notificationStore = useStore<NotificationStoreEntity>(GlobalStoreName.NOTIFICATION_STORE);

  /**
   * Get news list from CMS and prepare for using in frontend
   *
   * @param params
   */
  list(params: object = {}): Observable<any> {
    return this._contentUpdater.registerContent('news/list', params, () =>
      this._api.newsList(params).pipe(
        map(response => this._mapArticleList(this._mapper.mapCmsData(response.data.list, {
          id: 'id',
          slug: 'alias',
          type: 'type',
          publishAt: 'publishAt.date'
        }), response?.data?.countTotal))
      )
    );
  }

  public getBlogNotifications() {
    if (!this._platform.isBrowser) return;
    this._user.auth$
      .pipe(
        switchMap((auth) => {
          if (!auth) return EMPTY;
          return this._listenBlogNotifications();
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  /**
   * Get news item from CMS and prepare for using in frontend
   *
   * @param params
   */
  item(params: object = {}): Observable<any> {
    return this._contentUpdater.registerContent('news/item', params, () =>
      this._api.newsItem(params).pipe(
        map(response => {
          if (response.statusCode === 200) {
            return this._mapArticleList(
              this._mapper.mapCmsData(response.data, {
                id: 'id',
                slug: 'alias',
                type: 'type',
                publishAt: 'publishAt.date',
                unpublishAt: 'unpublishAt.date',
              }),
            );
          } else {
            return [
              {
                statusCode: response.statusCode,
              },
            ];
          }
        }),
      ),
    );
  };

  private _listenBlogNotifications() {
    return this.list({ type: BlogCategoryEnum.NOTIFICATIONS }).pipe(
      switchMap((data) => {
        if (!Array.isArray(data?.list)) return EMPTY;
        const sources: NotificationSocketType[] = data.list.map((article) => ({
          ...article,
          channel: CustomNotificationChanel.BLOG,
          title: article.Title,
          description: article.Description,
          image: article.Image,
          link: article.Link,
        }));
        const notifications$ = sources.length ? forkJoin(sources.map((source) => from(this._notificationFields.resolveNotificationFields(source)))) : of([]);
        return notifications$.pipe(
          tap((notifications) => this._clearBlogNotifications(notifications)),
          switchMap((notifications) => merge(...notifications.map((notification) => this._processNotification(notification)))),
        );
      }),
      catchError(() => EMPTY),
    );
  }

  private _clearBlogNotifications(notifications: NotificationType[]) {
    const now = Date.now();
    this._notificationStore.storeSignal.update((state) => ({
      ...state,
      list: state.list.filter((item) => {
        if (item.channel !== CustomNotificationChanel.BLOG && item.notify_channel !== CustomNotificationChanel.BLOG) return true;
        if (item.deleteTimestamp && item.deleteTimestamp <= now) return false;
        return item.isDeleted || notifications.some((notification) => notification.id === item.id && isTimeEligible(notification, now));
      }),
    }));
  }

  private _processNotification(notification: NotificationType) {
    const now = Date.now();
    const start = getStartMs(notification, now);
    const eligible = isTimeEligible(notification, now);
    if (!eligible && (start === null || start <= now)) return EMPTY;

    const show$ = eligible ? of(0) : timer(start - now);
    return show$.pipe(
      filter(() => isTimeEligible(notification)),
      tap(() => this._notifications.addNotification(notification, { ignoreExpired: true })),
      switchMap(() => {
        const end = getEndMs(notification);
        if (end === null) return EMPTY;
        return timer(Math.max(0, end - Date.now())).pipe(tap(() => this._removeNotifFromStore(notification.id)));
      }),
    );
  }

  private _removeNotifFromStore(id: string) {
    this._notificationStore.storeSignal.update((state) => ({
      ...state,
      list: state.list.filter((notification) => notification.id !== id),
    }));
  }

  /**
   * Prepare article list for using in frontend
   *
   * @param list
   * @private
   */
  private _mapArticleList(list: any[], countTotal?: number): any {
    const result: {list?: any[], countTotal?: number} = {
      countTotal
    };
    result.list = list.map(article => ({
      ...article,
      video: this._prepareVideoSrc(article.Video),
      published: this._getDateLabel(article.publishAt)
    }));

    return result;
  }

  /**
   * Returns correct video src by provided Vimeo link
   *
   * @param src
   * @private
   */
  private _prepareVideoSrc(src: string): SafeResourceUrl {
    if (!src) {
      return;
    }

    const videoId = src.split('/').pop();

    return this._sanitizer.bypassSecurityTrustResourceUrl(`https://player.vimeo.com/video/${videoId}?title=0&byline=0&portrait=0`);
  }

  /**
   * Returns correct date label by provided date string
   *
   * @param date
   * @private
   */
  private _getDateLabel(date: string): {num: number|string, label: string, date: Date} {
    const daysCount = this._time.timeDiff(this._time.toLocalDate(date)).days;
    const label = daysCount > 6
      ? this._time.toLocalDate(date).toString()
      : daysCount === 0
        ? 't.today'
        : daysCount === 1
          ? 't.yesterday'
          : 't.days-ago';

    return {
      num: daysCount > 1 ? daysCount : '',
      label,
      date: this._time.toLocalDate(date)
    };
  }
}
